import { useProgramme } from "@components/__templates__/ProgrammeDetailsPage/ProgrammeDetailsProvider";
import C72_ProgrammeDetailsBar from "@components/C72_ProgrammeDetailsBar/C72_ProgrammeDetailsBar";
import Cta from "@components/Cta/Cta";
import Link from "@components/Link/Link";
import { TypeProgrammeDetailsBarSkeleton } from "@customTypes/contentful";
import { GeneralLink } from "@customTypes/generalLink";
import { ProgrammeCTAType } from "@customTypes/ProgrammeCTAType";
import { useSettings } from "@utilities/context/settings";
import { COVEO_IGNORE_CLASS } from "@utilities/coveoConst";
import { SUB_NAV_ANCHOR } from "@utilities/e2e/e2eAnchorsList";
import { getE2EAnchorAttribute } from "@utilities/e2e/getE2EAnchor";
import { useDevice } from "@utilities/react/get-device/get-device";
import clsx from "clsx";
import debounce from "lodash/debounce";
import { useRouter } from "next/router";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useProgrammeDetailsPage } from "../../context/programmeDetailsPage";
import { SubNavLinkContext } from "../../context/subNavLinks";
import { UserContext } from "../../context/user";
import Arrow from "../../public/assets2019/img/svg/arrow-carousel.svg";
import DownloadTypes from "../../types/DownloadTypes";
import styles from "./C03_SubNavigation.module.scss";
import { useApplicationLink } from "@utilities/applicationLinkHelper";

export interface SubNavigationProps {
  content: {
    ctaLink1: GeneralLink;
    ctaLink2: GeneralLink;
    ctaLink2HelpText?: string;
    showApplyNowCta?: boolean;
    showDownloadCta?: boolean;
    showEnquiryCta?: boolean;
    programmeDetailsBar?: TypeProgrammeDetailsBarSkeleton;
  };
}

const SubNavigation: React.FC<SubNavigationProps> = (props) => {
  const stickyRef = useRef<HTMLDivElement | null>(null);
  const navigationRef = useRef<HTMLDivElement | null>(null);
  const stickComboRef = useRef<HTMLDivElement | null>(null);
  const stickComboWrapRef = useRef<HTMLDivElement | null>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [activeAnchorId, setActiveAnchorId] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [initialHeight, setInitialHeight] = useState(0);
  const stickUpdate = useRef(0);

  const router = useRouter();

  const [referPage, setReferPage] = useState("");
  useEffect(() => {
    setReferPage(router.asPath);
  }, [router]);

  const { content } = props;
  const {
    ctaLink1,
    ctaLink2,
    ctaLink2HelpText,
    showDownloadCta,
    showApplyNowCta,
    showEnquiryCta,
    programmeDetailsBar,
  } = content;

  const { user } = useContext(UserContext);

  const navigationLinks = useContext(SubNavLinkContext);

  const scrollTo = (el, anchorId) => {
    let sectionHead: HTMLElement = null;
    try {
      sectionHead = document.querySelector(anchorId);
    } catch (_err) {
      // selector is not corrent
    }
    const container = stickyRef.current;
    if (sectionHead) {
      const focusTimeout =
        Math.abs(sectionHead.getBoundingClientRect().top / 2) +
        (document.body.clientWidth > 1023 ? 200 : 500);

      setTimeout(
        () => {
          window.scroll({
            top:
              sectionHead.getBoundingClientRect().top +
              (window.scrollY || document.documentElement.scrollTop) -
              container.getBoundingClientRect().height,
            left: 0,
            behavior: "smooth",
          });
        },
        document.body.clientWidth > 1023 ? 0 : 500
      );

      setTimeout(() => {
        sectionHead.focus();
      }, Math.abs(focusTimeout));

      history.replaceState({}, "", el.getAttribute("href"));
    }
  };

  const device = useDevice();
  const isMobile = device === "mobile";

  const onNavClick = (e, hrefValue) => {
    e.stopPropagation();

    if (isMobile) {
      setIsExpanded(false);
    }

    const anchorId = hrefValue.substring(hrefValue.lastIndexOf("#"));

    setActiveAnchorId(hrefValue.split("#")[1]);

    if (anchorId.charAt(0) === "#") {
      e.preventDefault();
      scrollTo(e.target, anchorId);
    }
  };

  const LiJSX = ({
    linkText,
    openInNewTab,
    url,
    contentReference,
  }: {
    url?: string;
    linkText: string;
    openInNewTab?: boolean;
    contentReference?: { fields: { slug: string } };
  }) => {
    const linkUrl = contentReference?.fields?.slug
      ? `/${contentReference.fields.slug}`
      : url;

    const linkTarget = openInNewTab ? { target: "_blank" } : null;
    const activeUrl = activeAnchorId
      ? activeAnchorId
      : router["asPath"].split("#")[1];
    return (
      <li className={clsx(activeUrl === url?.split("#")[1] && styles.active)}>
        <Link
          href={linkUrl || "#"}
          {...linkTarget}
          onClick={(e) => onNavClick(e, linkUrl)}
        >
          {linkText}
        </Link>
      </li>
    );
  };

  useEffect(() => {
    const wrapper = stickyRef.current.querySelector(`.wrapper`);
    const condensedNavHeight = wrapper.getBoundingClientRect().height;

    setInitialHeight(condensedNavHeight);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      debounce(([e]) => {
        const componentHeight = e.boundingClientRect.height;
        const y = e.boundingClientRect.y + componentHeight;
        const isStick = e.intersectionRatio < 1 && y < window.innerHeight;

        setIsSticky((value) => {
          const now = new Date().getTime();
          if (now - stickUpdate.current < 300) {
            return value;
          }

          if (value !== isStick) {
            stickUpdate.current = now;
          }
          return isStick;
        });
      }, 100),
      { threshold: [1] }
    );
    observer.observe(stickComboRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const wrapper = stickyRef.current.querySelector(
      ".wrapper"
    ) as HTMLDivElement;
    const primaryList = stickyRef.current.querySelector(".subnav-links");
    const accordionButton = stickyRef.current.querySelector(
      `.${styles["accordion-button"]}`
    );
    const accordionButtonHeight =
      accordionButton?.getBoundingClientRect?.()?.height + 15 || 0;

    const buttons = stickyRef.current.querySelector(".button-wrappers");
    const listHeight = primaryList.getBoundingClientRect().height;
    const buttonsHeight = buttons.getBoundingClientRect().height;
    if (device === "tablet" || isMobile) {
      if (isExpanded) {
        wrapper.style.height =
          accordionButtonHeight + listHeight + buttonsHeight + "px";
      } else {
        wrapper.style.height = initialHeight + "px";
      }
    } else {
      wrapper.style.height = "auto";
    }
  }, [isExpanded, device]);

  const { programmeTypeCode, programmeTypeGroup } = useProgramme();

  const { siteSettings } = useSettings();
  const downloadBrochureSlug =
    siteSettings?.programmeCtaPagesCollection?.items.find(
      (item) =>
        item.type.toLowerCase() ===
        DownloadTypes.DOWNLOAD_BROCHURE.toLowerCase()
    );

  const { programmeApplyOnlineParameterValue, applicationInSalesforce,type } =
    useProgrammeDetailsPage();

  const apply = siteSettings?.programmeCtaPagesCollection?.items.filter(
    ({ type }) => type === ProgrammeCTAType.Apply
  )[0];

  return (
    <div
      className={clsx(
        styles.component,
        styles["subnav-combo"],
        programmeDetailsBar && styles["subnav-combo--with-bar"],
        !programmeDetailsBar && styles["subnav-combo--no-bar"],
        "blue",
        isSticky && styles["stick-combo"]
      )}
      ref={stickComboRef}
    >
      <div
        ref={stickComboWrapRef}
        className={clsx(styles["sticky-combo-wrap"])}
      >
        {programmeDetailsBar && (
          <C72_ProgrammeDetailsBar
            isSticky={isSticky}
            content={programmeDetailsBar}
          />
        )}
        <div
          ref={navigationRef}
          className={clsx(
            styles.component,
            styles["sub-navigation-two-level"],
            "blue",
            isExpanded && styles.open,
            isExpanded && styles["subnav-open"],
            isExpanded && styles["sticky-combo-wrap--open"],
            COVEO_IGNORE_CLASS
          )}
        >
          <div ref={stickyRef} className={styles["sticky-wrap"]}>
            <nav
              className={clsx("wrapper", styles.wrapper, styles["nav-wrapper"])}
            >
              <button
                className={clsx(
                  "flex justify-between w-full mb-0",
                  styles["accordion-button"],
                  isExpanded && styles["expanded"]
                )}
                aria-expanded={isExpanded}
                onClick={() => setIsExpanded((prev) => !prev)}
              >
                <span>Go to...</span>
                <i className="fill-current">
                  <Arrow aria-hidden />
                </i>
              </button>
              <ul
                className={clsx(styles["subnav-links"], "subnav-links")}
                hidden={!isExpanded && isMobile}
              >
                {navigationLinks.map((link, i) => {
                  if (link && (link.menuTitle || link.pageTitle)) {
                    return (
                      <React.Fragment
                        key={`${link.menuTitle || link.pageTitle}-${i}}`}
                      >
                        <LiJSX
                          linkText={link.menuTitle || link.pageTitle}
                          url={`#${link.anchorKey}`}
                        />
                      </React.Fragment>
                    );
                  }
                })}
              </ul>
              <div
                className={clsx(styles["button-wrappers"], "button-wrappers")}
              >
                {showApplyNowCta && (
                  <Cta
                    id="apply-now"
                    linkText="Apply now"
                    type="primary"
                    light
                    url={
                      useApplicationLink({user: !!user,programmeApplyOnlineId: programmeApplyOnlineParameterValue,isSalesforceApplication: applicationInSalesforce})
                    }
                  />
                )}
                {!showApplyNowCta && ctaLink1 && (
                  <Cta
                    id="cta-link1"
                    {...ctaLink1.fields}
                    type="primary"
                    light
                  />
                )}
                {showDownloadCta && programmeTypeCode && (
                  <div className={styles.subNavTertiary}>
                    <Cta
                      id="download-brochure"
                      {...getE2EAnchorAttribute(
                        SUB_NAV_ANCHOR,
                        "download-brochure-link"
                      )}
                      url={`${downloadBrochureSlug?.url.slug}?programmeTypeCode=${programmeTypeCode}&programmeTypeGroup=${programmeTypeGroup}&programType=${type}&brochureType=Download a brochure&referPage=${referPage}`}
                      linkText="Download brochure"
                      type="tertiary"
                      light
                      icon="icon-arrow"
                    />
                  </div>
                )}
                {showDownloadCta && !programmeTypeCode && ctaLink2 && (
                  <div className={styles.subNavTertiary}>
                    <Cta
                      id="cta-link2"
                      {...getE2EAnchorAttribute(
                        SUB_NAV_ANCHOR,
                        "download-brochure-link"
                      )}
                      url={`${downloadBrochureSlug?.url.slug}${ctaLink2.fields?.url}`}
                      type="tertiary"
                      light
                      icon="icon-arrow"
                      linkText="Download brochure"
                    />
                  </div>
                )}
                {!showDownloadCta && ctaLink2 && (
                  <div className={styles.subNavTertiary}>
                    <Cta
                      id="cta-link3"
                      {...ctaLink2.fields}
                      type="tertiary"
                      light
                      icon="icon-arrow"
                      helpText={ctaLink2HelpText}
                    />
                  </div>
                )}
                {showEnquiryCta && (
                  <div className={styles.subNavTertiary}>
                    <Cta
                      id="cta-enquiry"
                      url="#Model.EnquiryLink"
                      linkText="EnquiryLabel"
                      type="tertiary"
                      light
                      icon="icon-arrow"
                    />
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubNavigation;
